/**
 * Synthesised sounds, no audio files: a quiet institute hum, door chime, UI click, teleport
 * whoosh. The AudioContext is created on the first user gesture (the landing card button).
 */
export class AudioEngine {
  private ctx: AudioContext | undefined;
  private master: GainNode | undefined;
  private hum: { gain: GainNode; nodes: AudioNode[] } | undefined;
  private muted = false;

  /** Call from a user gesture. Safe to call repeatedly. */
  start(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.8;
    this.master.connect(this.ctx.destination);
  }

  get ready(): boolean {
    return !!this.ctx;
  }

  get context(): AudioContext | undefined {
    return this.ctx;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx)
      this.master.gain.setTargetAtTime(muted ? 0 : 0.8, this.ctx.currentTime, 0.05);
  }

  /** Institute ambience: filtered brown noise with a slow LFO on the filter. */
  setHum(on: boolean, level = 0.035): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    if (on && !this.hum) {
      const seconds = 4;
      const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      let last = 0;
      for (let i = 0; i < data.length; i++) {
        const white = Math.random() * 2 - 1;
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.5;
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 220;
      filter.Q.value = 0.7;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.08;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 60;
      lfo.connect(lfoGain).connect(filter.frequency);
      const drone = ctx.createOscillator();
      drone.type = 'sine';
      drone.frequency.value = 55;
      const droneGain = ctx.createGain();
      droneGain.gain.value = 0.25;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(filter).connect(gain);
      drone.connect(droneGain).connect(gain);
      gain.connect(this.master);
      source.start();
      lfo.start();
      drone.start();
      gain.gain.setTargetAtTime(level, ctx.currentTime, 1.5);
      this.hum = { gain, nodes: [source, lfo, drone] };
    } else if (!on && this.hum) {
      const hum = this.hum;
      this.hum = undefined;
      hum.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
      setTimeout(() => {
        for (const n of hum.nodes) (n as AudioScheduledSourceNode).stop();
        hum.gain.disconnect();
      }, 3000);
    }
  }

  private tone(
    freq: number,
    start: number,
    duration: number,
    peak: number,
    type: OscillatorType = 'sine',
  ): void {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain).connect(this.master);
    osc.start(start);
    osc.stop(start + duration + 0.05);
  }

  /** Soft two-note door chime. */
  chime(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(880, t, 0.7, 0.18);
    this.tone(1318.5, t + 0.16, 0.9, 0.14);
    this.tone(440, t, 0.5, 0.05, 'triangle');
  }

  /** Short UI click. */
  click(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(1800, t, 0.05, 0.08, 'square');
    this.tone(900, t, 0.06, 0.04);
  }

  /** Key press on the virtual keyboard. */
  key(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(1400 + Math.random() * 200, t, 0.035, 0.05, 'triangle');
  }

  /** Teleport: a quick filtered noise sweep. */
  whoosh(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.35, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.2;
    filter.frequency.setValueAtTime(400, t);
    filter.frequency.exponentialRampToValueAtTime(2400, t + 0.3);
    const gain = ctx.createGain();
    gain.gain.value = 0.12;
    source.connect(filter).connect(gain).connect(this.master);
    source.start(t);
  }

  /** Door sliding open: low noise swell. */
  doorSlide(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.9, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.09, t + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(t);
  }
}
